import { getGlobal } from "@RHCommerceDev/utils-globals/getGlobal";
import parseUrlPath from "./parse-url-path";

const env = getGlobal("_env");

/**
 * Processes a URL to apply locale, BC/TN hacks, and shop UI transformations
 * @param targetUrl - The URL to process
 * @param locale - The locale string to prepend if needed
 * @param isConcierge - Whether this is a concierge request
 * @returns The processed URL
 */
const getLocalizedUrl = (
  targetUrl: string,
  locale: string,
  isConcierge: boolean
): string => {
  // SSR-safe hostname extraction
  const getHostname = (): string | null => {
    if (typeof window === "undefined") {
      return env.ENV_DOMAIN;
    }

    try {
      return new URL(window.location.origin).hostname;
    } catch {
      return null;
    }
  };

  // Get current origin for concierge URLs
  const getCurrentOrigin = (): string | null => {
    if (typeof window === "undefined") {
      return `https://${env.ENV_DOMAIN}`;
    }

    try {
      return window.location.origin;
    } catch {
      return null;
    }
  };

  // Get environment hostname (SSR-safe)
  const envHostname = getHostname();
  const currentOrigin = getCurrentOrigin();

  // Apply BC and TN hacks if we have an environment hostname
  let processedUrl = targetUrl;

  if (envHostname) {
    // Handle concierge URLs for BC and TN
    if (isConcierge && currentOrigin) {
      // For concierge, create paths like ${currentOrigin}${locale}/tn and ${currentOrigin}${locale}/bc
      if (
        targetUrl.includes("rhteen.stg2.rhnonprod.com") ||
        targetUrl.includes("/rhteen")
      ) {
        processedUrl = `${currentOrigin}${locale}/tn`;
      } else if (
        targetUrl.includes("rhbabyandchild.stg2.rhnonprod.com") ||
        targetUrl.includes("/babyandchild")
      ) {
        processedUrl = `${currentOrigin}${locale}/bc`;
      }
      // Handle rh.com URLs with specific baby and teen paths for concierge
      else if (
        targetUrl.includes("rh.com") &&
        (targetUrl.includes("/babyandchild") || targetUrl.includes("/teen"))
      ) {
        if (targetUrl.includes("/babyandchild")) {
          processedUrl = `${currentOrigin}${locale}/bc`;
        } else if (targetUrl.includes("/teen")) {
          processedUrl = `${currentOrigin}${locale}/tn`;
        }
      }
    } else {
      // non-concierge URLs
      // Hack for rhteen and rhbabyandchild URLs
      // "https://rhteen.stg2.rhnonprod.com/"
      // "https://rhbabyandchild.stg2.rhnonprod.com/"
      if (targetUrl.includes("rhteen.stg2.rhnonprod.com")) {
        processedUrl = targetUrl.replace(
          "rhteen.stg2.rhnonprod.com",
          `rhteen.${envHostname}`
        );
      } else if (targetUrl.includes("rhbabyandchild.stg2.rhnonprod.com")) {
        processedUrl = targetUrl.replace(
          "rhbabyandchild.stg2.rhnonprod.com",
          `rhbabyandchild.${envHostname}`
        );
      }
      // Hack for rh.com URLs with specific baby and teen paths
      // "https://rh.com/us/en/babyandchild"
      // "https://rh.com/us/en/teen"
      else if (
        targetUrl.includes("rh.com") &&
        (targetUrl.includes("/babyandchild") || targetUrl.includes("/teen"))
      ) {
        processedUrl = targetUrl.replace("rh.com", envHostname);
      }
    }
  }

  // Apply locale logic (only for relative paths)
  if (processedUrl.startsWith("/")) {
    // Regex for valid locale combinations: /(gb|us|ca|de|be|es|fr)/(en|fr|nl|de|es)/
    const validLocalePattern =
      /^(\/(?:gb|us|ca|de|be|es|fr)\/(?:en|fr|nl|de|es))\/?/;
    const hasValidLocale = validLocalePattern.test(processedUrl);

    if (!hasValidLocale) {
      processedUrl = `${locale}${processedUrl}`;
    }
  }

  // Apply shop UI parsing if feature flag is enabled
  return env.FEATURE_SHOP_UI ? parseUrlPath(processedUrl) : processedUrl;
};

export default getLocalizedUrl;
